/**
 * Logger utility for structured logging across the application.
 * Provides environment-aware logging with proper log levels.
 */
import { isDev, isTest } from '@/utils/environment';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Formats log messages consistently
 */
function formatMessage(level: LogLevel, message: string, context?: unknown): unknown[] {
  const timestamp = new Date().toISOString();
  const formatted = `[${timestamp}] [${level.toUpperCase()}] ${message}`;
  return context !== undefined ? [formatted, context] : [formatted];
}

/**
 * Application logger with environment-aware log levels
 */
export const logger = {
  /**
   * Debug-level logging (only in development)
   * Use for detailed debugging information
   */
  debug: (messageOrData: string | unknown, context?: unknown) => {
    if (isDev && !isTest) {
      if (typeof messageOrData === 'string') {
        console.debug(...formatMessage('debug', messageOrData, context));
      } else {
        // If first arg is not a string, just log it directly
        console.debug(messageOrData, context);
      }
    }
  },

  /**
   * Info-level logging (only in development)
   * Use for general informational messages
   */
  info: (messageOrData: string | unknown, context?: unknown) => {
    if (isDev && !isTest) {
      if (typeof messageOrData === 'string') {
        console.info(...formatMessage('info', messageOrData, context));
      } else {
        console.info(messageOrData, context);
      }
    }
  },

  /**
   * Warning-level logging (all environments)
   * Use for non-critical issues that should be investigated
   */
  warn: (messageOrData: string | unknown, context?: unknown) => {
    if (!isTest) {
      if (typeof messageOrData === 'string') {
        console.warn(...formatMessage('warn', messageOrData, context));
      } else {
        console.warn(messageOrData, context);
      }
      // TODO: Integrate with monitoring service (e.g., Sentry)
    }
  },

  /**
   * Error-level logging (all environments)
   * Use for errors that require immediate attention
   */
  error: (
    messageOrError: string | Error | unknown,
    errorOrContext?: Error | unknown,
    context?: unknown,
  ) => {
    if (!isTest) {
      // Case 1: logger.error('message', error, context)
      if (typeof messageOrError === 'string') {
        if (errorOrContext instanceof Error) {
          const logContext = {
            ...(typeof context === 'object' && context !== null ? context : {}),
            error: {
              name: errorOrContext.name,
              message: errorOrContext.message,
              stack: errorOrContext.stack,
            },
          };
          console.error(...formatMessage('error', messageOrError, logContext));
        } else {
          console.error(...formatMessage('error', messageOrError, errorOrContext));
        }
      }
      // Case 2: logger.error(error) - just the error object
      else {
        if (messageOrError instanceof Error) {
          console.error(messageOrError.message, {
            error: {
              name: messageOrError.name,
              message: messageOrError.message,
              stack: messageOrError.stack,
            },
          });
        } else {
          console.error(messageOrError, errorOrContext);
        }
      }
      // TODO: Integrate Sentry
    }
  },

  /**
   * Log a value for inspection (development only)
   * Use for quick debugging of variable values
   */
  log: (...args: unknown[]) => {
    if (isDev && !isTest) {
      console.log(...args);
    }
  },
};
